import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import {
  AutocompleteCDS,
  DateFieldCDS,
  onAutocompleteChangeInForm,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { MIN_CALENDAR_DATE, TODAY_IN_DAYS } from 'constants/date';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';
import { getLoanRateSheetsAction } from 'store/loanApplications/actions';
import { selectSortedRateSheets } from 'store/loanApplications/selectors';

import { ProgramsAndPricingTestIds } from '../../ProgramsAndPricing.testIds';

import { rateSheetFormControlsStyles } from './ProgramsTableToolbar.styles';
import { filterRateSheetByDate } from './ProgramsTableToolbar.utils';

const RateSheetForm = ({ isLoading, setIsLoading, loanId, setTempRateSheetId, rateSheetId }) => {
  const dispatch = useDispatch();

  const rateSheetList = useSelector(selectSortedRateSheets);

  const { form, validationErrors, setInForm, isFormChanged, validateField, setForm, setIsTouched } =
    useForm({});

  const isRateSheetChanged = rateSheetId !== form.rateSheetId?.value;

  const filteredRateSheetList = useMemo(
    () =>
      rateSheetList
        .filter(filterRateSheetByDate(form.fromDate, form.toDate))
        .map(({ title: name, rateSheetId: value }) => ({ name, value })),
    [form.fromDate, rateSheetList, form.toDate],
  );

  const onDateChange = useCallback(
    (date, name) => {
      const dateMoment = dayjs(date);

      if (dateMoment.isValid()) {
        setIsTouched(true);
        setForm(prev => ({ ...prev, [name]: dateMoment }));
      }
    },
    [setForm, setIsTouched],
  );

  const onDateFocusInOut = useCallback(
    (date, name) => {
      const dateMoment = dayjs(date);

      setForm(prev => ({
        ...prev,
        [name]: dateMoment.isValid() ? dateMoment : null,
      }));
    },
    [setForm],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    dispatch(getLoanRateSheetsAction({ loanId }))
      .then(({ payload }) => {
        const {
          lifeTimeInfo: { createdDate, modifiedDate },
          title,
        } = _.find(payload, i => i.rateSheetId === rateSheetId);

        setForm({
          rateSheetId: { name: title, value: rateSheetId },
          fromDate: modifiedDate || createdDate,
          toDate: modifiedDate || createdDate,
        });
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, loanId, setIsLoading, rateSheetId, setForm]);

  useEffect(() => {
    const isFromDateInvalid = !form.fromDate && form.toDate;
    const isToDateInvalid = form.fromDate && !form.toDate;

    if (isFromDateInvalid) {
      onDateChange(form.toDate, 'fromDate');
    }

    if (isToDateInvalid) {
      onDateChange(TODAY_IN_DAYS, 'toDate');
    }
  }, [form.toDate, form.fromDate, onDateChange]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      setTempRateSheetId(form.rateSheetId?.value);
      dispatch(closeDialogAction());
    },
    [dispatch, setTempRateSheetId, form.rateSheetId?.value],
  );

  const onCancel = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="grid gap-24 p-24"
      data-ischanged={isFormChanged}
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        disableClearable
        required
        data-testid={ProgramsAndPricingTestIds.RATE_SHEET_ID_SELECT}
        dataTestIdInput={ProgramsAndPricingTestIds.RATE_SHEET_FORM_RATE_SHEET_ID_AUTOCOMPLETE_INPUT}
        dataTestIdOption={ProgramsAndPricingTestIds.RATE_SHEET_FORM_RATE_SHEET_ID_OPTION}
        disabled={isLoading || !form.toDate || !form.fromDate}
        error={!!validationErrors.rateSheetId}
        helperText={validationErrors.rateSheetId}
        id="rateSheetId"
        label="Rate Sheet Title"
        name="rateSheetId"
        options={filteredRateSheetList}
        placeholder="Please enter"
        value={form.rateSheetId}
        variant="outlined"
        onChange={onAutocompleteChange}
      />

      <div className="grid grid-cols-2 gap-x-24">
        <DateFieldCDS
          required
          dataTestId={ProgramsAndPricingTestIds.RATE_SHEET_FORM_FROM_DATE_INPUT}
          disabled={isLoading}
          label="From"
          maxDate={form.toDate}
          minDate={MIN_CALENDAR_DATE}
          name="fromDate"
          placeholder="Select day"
          value={form.fromDate}
          width="auto"
          onBlur={onDateFocusInOut}
          onChange={onDateChange}
          onFocus={onDateFocusInOut}
        />

        <DateFieldCDS
          required
          dataTestId={ProgramsAndPricingTestIds.RATE_SHEET_FORM_TO_DATE_INPUT}
          disabled={isLoading}
          label="To"
          maxDate={TODAY_IN_DAYS}
          minDate={form.fromDate}
          name="toDate"
          placeholder="Select day"
          value={form.toDate}
          width="auto"
          onBlur={onDateFocusInOut}
          onChange={onDateChange}
          onFocus={onDateFocusInOut}
        />
      </div>

      <Box sx={rateSheetFormControlsStyles}>
        <Button
          data-testid={ProgramsAndPricingTestIds.RATE_SHEET_FORM_CANCEL_BUTTON}
          disabled={isLoading}
          size="medium"
          variant="line"
          onClick={onCancel}
        >
          Cancel
        </Button>

        <Button
          data-testid={ProgramsAndPricingTestIds.RATE_SHEET_FORM_CONFIRM_BUTTON}
          disabled={isLoading || !isRateSheetChanged}
          size="medium"
          type="submit"
        >
          Confirm
        </Button>
      </Box>
    </form>
  );
};

RateSheetForm.propTypes = {
  ...WithLoadingPropTypes,
  setTempRateSheetId: PropTypes.func.isRequired,
  loanId: PropTypes.string,
  rateSheetId: PropTypes.string,
};

export default compose(withLoading, memo)(RateSheetForm);
